import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { EventCard } from "../components/EventCard";
import GoogleEventsMap from "../components/GoogleEventsMap";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkle, MagnifyingGlass, X, MapTrifold, ListDashes } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("list"); // list | map
  const [loading, setLoading] = useState(true);
  const [nlQuery, setNlQuery] = useState("");
  const [nlLoading, setNlLoading] = useState(false);
  const [aiFilters, setAiFilters] = useState(null);

  const category = searchParams.get("category") || "";
  const brand = searchParams.get("brand") || "";
  const city = searchParams.get("city") || "";
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const q = searchParams.get("q") || "";
  const nl = searchParams.get("nl") || "";

  const [brandList, setBrandList] = useState({});
  useEffect(() => {
    api.get("/categories").then(({ data }) => setBrandList(data.brands_by_category)).catch(() => {});
  }, []);

  const brandsForCategory = useMemo(() => {
    if (category && brandList[category]) return brandList[category];
    return Array.from(new Set(Object.values(brandList).flat())).sort();
  }, [category, brandList]);

  useEffect(() => {
    if (nl) {
      // Run AI search
      (async () => {
        setNlLoading(true);
        try {
          const { data } = await api.post("/search/nl", { query: nl });
          setEvents(data.events);
          setAiFilters(data.filters);
          setLoading(false);
        } catch {
          toast.error("Ricerca AI non disponibile");
        } finally {
          setNlLoading(false);
        }
      })();
    } else {
      setAiFilters(null);
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (brand) params.set("brand", brand);
      if (city) params.set("city", city);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (q) params.set("q", q);
      setLoading(true);
      api.get(`/events?${params.toString()}`).then(({ data }) => {
        setEvents(data.events);
      }).catch(() => toast.error("Errore caricamento")).finally(() => setLoading(false));
    }
  }, [category, brand, city, dateFrom, dateTo, q, nl]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    p.delete("nl");
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
    setNlQuery("");
  };

  const clearAll = () => {
    setSearchParams({});
    setNlQuery("");
  };

  const handleNL = async (e) => {
    e.preventDefault();
    if (!nlQuery.trim()) return;
    const p = new URLSearchParams();
    p.set("nl", nlQuery);
    setSearchParams(p);
  };

  const hasFilters = category || brand || city || dateFrom || dateTo || q || nl;

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-3">Discover</div>
        <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter mb-6" data-testid="events-title">Trova il tuo raduno</h1>

        {/* NL Search */}
        <form onSubmit={handleNL} className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500 mb-2 flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="text-primary" /> Ricerca in linguaggio naturale
          </div>
          <div className="flex flex-col sm:flex-row gap-2 max-w-3xl">
            <Input
              data-testid="events-nl-input"
              value={nlQuery}
              onChange={(e) => setNlQuery(e.target.value)}
              placeholder='es: "raduni Harley in Italia a settembre"'
              className="flex-1 h-12 bg-card border-border focus:border-primary rounded-sm"
            />
            <Button type="submit" disabled={nlLoading} data-testid="events-nl-btn" className="h-12 px-6 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider rounded-sm">
              {nlLoading ? "AI..." : "Cerca AI"}
            </Button>
          </div>
          {aiFilters && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Interpretato:</span>
              {Object.entries(aiFilters).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} className="text-[10px] px-2 py-1 border border-primary/40 text-primary uppercase tracking-widest">{k}: {String(v)}</span>
              ))}
            </div>
          )}
        </form>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <Select value={category || "all"} onValueChange={(v) => updateFilter("category", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category" className="bg-card border-border rounded-sm h-11 text-xs uppercase tracking-wider">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">Tutte</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={brand || "all"} onValueChange={(v) => updateFilter("brand", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-brand" className="bg-card border-border rounded-sm h-11 text-xs uppercase tracking-wider">
              <SelectValue placeholder="Marca" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-72">
              <SelectItem value="all">Tutte</SelectItem>
              {brandsForCategory.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-city" placeholder="Città" value={city} onChange={(e) => updateFilter("city", e.target.value)} className="bg-card border-border rounded-sm h-11 text-sm" />
          <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => updateFilter("date_from", e.target.value)} className="bg-card border-border rounded-sm h-11 text-sm" />
          <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => updateFilter("date_to", e.target.value)} className="bg-card border-border rounded-sm h-11 text-sm" />
          <Input data-testid="filter-q" placeholder="Cerca..." value={q} onChange={(e) => updateFilter("q", e.target.value)} className="bg-card border-border rounded-sm h-11 text-sm" />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {hasFilters && (
              <button onClick={clearAll} data-testid="clear-filters-btn" className="text-xs font-bold uppercase tracking-widest text-neutral-400 hover:text-primary flex items-center gap-1">
                <X size={12} weight="bold" /> Reset
              </button>
            )}
            <span className="text-xs text-neutral-500 uppercase tracking-widest">{loading ? "Caricamento..." : `${events.length} eventi`}</span>
          </div>
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button onClick={() => setView("list")} data-testid="view-list" className={`px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${view === "list" ? "bg-primary text-white" : "text-neutral-400 hover:text-white"}`}>
              <ListDashes size={12} weight="bold" /> Lista
            </button>
            <button onClick={() => setView("map")} data-testid="view-map" className={`px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${view === "map" ? "bg-primary text-white" : "text-neutral-400 hover:text-white"}`}>
              <MapTrifold size={12} weight="bold" /> Mappa
            </button>
          </div>
        </div>
      </div>

      {view === "map" ? (
        <GoogleEventsMap events={events} height="70vh" />
      ) : (
        <>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[16/12] bg-card border border-border rounded-sm animate-pulse" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-border rounded-sm">
              <MagnifyingGlass size={40} weight="bold" className="mx-auto text-neutral-600 mb-4" />
              <p className="text-neutral-400 uppercase tracking-widest text-sm">Nessun evento trovato</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {events.map((e, i) => <EventCard key={e.event_id} event={e} index={i} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
